// İSG uzmanı portalı — Kiosk cihaz kaydı (Modal + RHF + Zod). api-contract §5.9.1
// (`POST /auth/kiosk/register`). Form: ad (zorunlu) + saha (opsiyonel, `GET /sites`
// USelect → siteUuid) + rol(ler) (opsiyonel, `GET /admin/roles` çoklu seçim → roleUuids —
// kiosk yetkili rol, ör. "Kiosk Cihaz"). **companyId GÖNDERİLMEZ** (backend token'dan — §5.9.1).
//
// GİZLİ VERİ SINIRI (§5.9): Başarı yanıtındaki `activationCode` **tek-kullanımlıktır** ve
// **yalnız bu yanıtta** döner. Kod BÜYÜK gösterilir + kopyalanır + "yalnız bir kez gösterilir"
// uyarısı verilir. Kod **loglanmaz, kalıcı state'te tutulmaz** — modal kapanınca yerel state
// temizlenir (tek-sefer). Query cache'e yazılmaz; yalnız cihaz LİSTESİ invalidate edilir
// (liste activationCode taşımaz — §5.9.5).

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  EmptyState,
  Field,
  Input,
  type KioskRegisterResult,
  Modal,
  Select,
  type SelectOption,
  Spinner,
  adminApi,
  currentCulture,
  kioskApi,
  resolveApiErrorMessage,
  settingsApi,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, TriangleAlert } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { formatDateTime } from '../../lib/format';
import { kioskKeys } from '../../lib/kioskKeys';

/** Saha/rol seçicilerini tek sayfada besleyecek makul üst sınır (tenant ölçeği). */
const OPTIONS_PAGE_SIZE = 200;

interface KioskRegisterModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

/** Boş dizeyi `undefined`'a indirger (opsiyonel saha — gönderilmez). */
function orUndefined(value: string | undefined): string | undefined {
  return value && value.length > 0 ? value : undefined;
}

export function KioskRegisterModal({ open, onOpenChange, onSuccess }: KioskRegisterModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const culture = currentCulture();

  // Tek-kullanımlık kayıt sonucu (activationCode dahil) — YALNIZ yerel state; loglanmaz,
  // cache'e yazılmaz, modal kapanınca temizlenir (§5.9 tek-sefer).
  const [registered, setRegistered] = useState<KioskRegisterResult | null>(null);
  const [copied, setCopied] = useState(false);

  // Saha seçenekleri (opsiyonel) — Ayarlar §10 `GET /sites` (tenant-scoped; kanonik shape).
  const sitesQuery = useQuery({
    queryKey: ['kiosk', 'register-sites'] as const,
    queryFn: () =>
      settingsApi.listSites({
        page: 1,
        pageSize: OPTIONS_PAGE_SIZE,
        sortBy: 'name',
        sortDir: 'asc',
      }),
    enabled: open && registered === null,
    staleTime: 60_000,
  });

  // Rol seçenekleri (opsiyonel, çoklu) — §9.1 `GET /admin/roles`; yalnız aktif roller.
  const rolesQuery = useQuery({
    queryKey: ['kiosk', 'register-roles'] as const,
    queryFn: () =>
      adminApi.listRoles({ page: 1, pageSize: OPTIONS_PAGE_SIZE, sortBy: 'name', sortDir: 'asc' }),
    enabled: open && registered === null,
    staleTime: 60_000,
  });

  const siteOptions: SelectOption[] = useMemo(
    () => (sitesQuery.data?.items ?? []).map((s) => ({ value: s.uuid, label: s.name })),
    [sitesQuery.data],
  );
  const roleItems = useMemo(
    () => (rolesQuery.data?.items ?? []).filter((r) => r.isActive === true),
    [rolesQuery.data],
  );

  const schema = useMemo(
    () =>
      z.object({
        name: z.string().trim().min(1, t('kiosk.validation.nameRequired')),
        siteUuid: z.string().optional(),
        roleUuids: z.array(z.string()),
      }),
    [t],
  );
  type FormValues = z.infer<typeof schema>;

  const defaults: FormValues = { name: '', siteUuid: '', roleUuids: [] };

  const { register, handleSubmit, reset, control, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: defaults,
  });

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      kioskApi.register({
        name: values.name.trim(),
        // companyId GÖNDERİLMEZ (backend token'dan — §5.9.1). Saha opsiyonel; boşsa gönderilmez.
        siteUuid: orUndefined(values.siteUuid),
        roleUuids: values.roleUuids,
      }),
    onSuccess: (result) => {
      // Liste tazelenir (yeni cihaz Pending olarak görünür). activationCode cache'e YAZILMAZ.
      queryClient.invalidateQueries({ queryKey: kioskKeys.devices() });
      setRegistered(result);
      setCopied(false);
      reset(defaults);
      onSuccess?.();
    },
  });

  function closeAll(next: boolean) {
    if (!next) {
      // Tek-sefer temizlik: activationCode yerel state'ten silinir (kalıcı tutulmaz).
      setRegistered(null);
      setCopied(false);
      mutation.reset();
      reset(defaults);
    }
    onOpenChange(next);
  }

  async function copyCode(code: string) {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
    } catch {
      // Pano erişimi reddedilirse sessiz geçilir — kod ekranda görünür (kullanıcı elle alır).
      setCopied(false);
    }
  }

  // ---- Başarı görünümü: tek-kullanımlık activationCode (BÜYÜK) + kopyala + uyarı ----
  if (registered) {
    return (
      <Modal
        open={open}
        onOpenChange={closeAll}
        title={t('kiosk.register.successTitle')}
        description={t('kiosk.register.successDescription', { name: registered.name })}
        closeLabel={t('admin.common.close')}
        footer={<Button onClick={() => closeAll(false)}>{t('admin.common.close')}</Button>}
      >
        <div className="flex flex-col gap-4">
          <Alert tone="warning">
            <span className="flex items-start gap-2">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{t('kiosk.register.codeWarning')}</span>
            </span>
          </Alert>

          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-fg">{t('kiosk.register.codeLabel')}</span>
            <div className="flex items-center gap-2">
              <output className="flex-1 select-all rounded-md border border-border-strong bg-surface-muted px-4 py-3 text-center font-mono text-3xl font-semibold tracking-[0.35em] text-fg">
                {registered.activationCode}
              </output>
              <Button
                type="button"
                variant="secondary"
                onClick={() => copyCode(registered.activationCode)}
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4" aria-hidden="true" />
                    {t('kiosk.register.copied')}
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" aria-hidden="true" />
                    {t('kiosk.register.copy')}
                  </>
                )}
              </Button>
            </div>
          </div>

          <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-[auto,1fr]">
            <dt className="text-muted">{t('kiosk.register.deviceUuid')}</dt>
            <dd className="break-all font-mono text-xs text-fg sm:text-sm">
              {registered.deviceUuid}
            </dd>
            <dt className="text-muted">{t('kiosk.register.expiresAt')}</dt>
            <dd className="text-fg">
              {formatDateTime(registered.activationCodeExpiresAt, culture)}
            </dd>
          </dl>

          <p className="text-xs text-muted">{t('kiosk.register.nextStep')}</p>
        </div>
      </Modal>
    );
  }

  // ---- Kayıt formu ----
  return (
    <Modal
      open={open}
      onOpenChange={closeAll}
      title={t('kiosk.register.title')}
      description={t('kiosk.register.description')}
      closeLabel={t('admin.common.close')}
      footer={
        <>
          <Button variant="secondary" onClick={() => closeAll(false)} disabled={mutation.isPending}>
            {t('admin.common.cancel')}
          </Button>
          <Button type="submit" form="kiosk-register-form" loading={mutation.isPending}>
            {mutation.isPending ? (
              <Spinner label={t('kiosk.register.submit')} />
            ) : (
              t('kiosk.register.submit')
            )}
          </Button>
        </>
      }
    >
      <form
        id="kiosk-register-form"
        className="flex flex-col gap-4"
        onSubmit={handleSubmit((values) => mutation.mutate(values))}
        noValidate
      >
        {mutation.isError ? (
          <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
        ) : null}

        <Field
          htmlFor="kiosk-name"
          label={t('kiosk.register.field.name')}
          error={formState.errors.name?.message}
          required
        >
          <Input
            id="kiosk-name"
            invalid={Boolean(formState.errors.name)}
            placeholder={t('kiosk.register.field.namePlaceholder')}
            {...register('name')}
          />
        </Field>

        <Field
          htmlFor="kiosk-site"
          label={t('kiosk.register.field.site')}
          hint={t('kiosk.register.field.siteHint')}
        >
          <Select
            id="kiosk-site"
            disabled={sitesQuery.isPending}
            placeholder={t('kiosk.register.site.placeholder')}
            options={siteOptions}
            {...register('siteUuid')}
          />
        </Field>

        <Controller
          control={control}
          name="roleUuids"
          render={({ field }) => (
            <fieldset className="flex flex-col gap-1.5">
              <legend className="text-sm font-medium text-fg">
                {t('kiosk.register.field.roles')}
              </legend>
              <p className="text-xs text-muted">{t('kiosk.register.field.rolesHint')}</p>
              {rolesQuery.isPending ? (
                <Spinner label={t('admin.common.loading')} />
              ) : roleItems.length === 0 ? (
                <EmptyState variant="panel" title={t('kiosk.register.roles.empty')} />
              ) : (
                <div className="mt-1 flex max-h-48 flex-col gap-1.5 overflow-y-auto rounded-md border border-border bg-surface p-3">
                  {roleItems.map((role) => {
                    const checked = field.value.includes(role.uuid);
                    return (
                      <label key={role.uuid} className="flex items-start gap-2 text-sm text-fg">
                        <input
                          type="checkbox"
                          className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-primary)]"
                          checked={checked}
                          onChange={(e) => {
                            field.onChange(
                              e.target.checked
                                ? [...field.value, role.uuid]
                                : field.value.filter((u) => u !== role.uuid),
                            );
                          }}
                        />
                        <span className="flex flex-col gap-0.5">
                          <span className="font-medium">{role.name}</span>
                          <code className="text-xs text-muted">{role.value}</code>
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </fieldset>
          )}
        />
      </form>
    </Modal>
  );
}
